import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { submitConsultationLead } from '../services/frappeLeadService';

// Dropdown option sets
const countryOptions = ['Germany', 'Canada', 'Australia', 'United Kingdom', 'New Zealand', 'Other'];
const pathwayOptions = ['Study', 'Skilled Worker', 'Job Seeker (Chancenkarte)', 'Permanent Residency', 'Other'];
const packageOptions = ['DIY', 'Plus', 'PRO'];
const degreeOptions = ['High School', 'Diploma', "Bachelor's", "Master's", 'PhD', 'Other'];
const experienceYearsOptions = ['0', '1-2', '3-5', '6-10', '10+'];
const industryOptions = ['IT', 'Engineering', 'Business', 'Healthcare', 'Education', 'Other'];
const englishLevels = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const germanLevels = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const budgetOptions = ['< $10,000', '$10,000 - $20,000', '$20,000 - $30,000', '> $30,000'];
const sponsorOptions = ['Self-funded', 'Parent/Family', 'Bank Loan', 'Scholarship', 'Other'];

const initialForm = {
  first_name: '',
  last_name: '',
  email: '',
  mobile_no: '',
  custom_countryselection: '',
  notes: '',
  // Structured optional fields
  personal_age: '',
  personal_city: '',
  personal_citizenship: '',
  education_level: '',
  education_field: '',
  education_year: '',
  education_institution: '',
  experience_years: '',
  experience_role: '',
  experience_industry: '',
  lang_english: '',
  lang_german: '',
  lang_ielts: '',
  financial_budget: '',
  financial_sponsor: '',
  scoring_score: '',
  scoring_notes: '',
  custom_selected_pathway: '',
  custom_selected_package: '',
};
 

const ConsultationBookingPage = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState({ type: '', message: '' });
  const [cvFile, setCvFile] = useState(null);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (!form.first_name.trim()) return 'First name is required';
    if (!form.last_name.trim()) return 'Last name is required';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'Valid email is required';
    if (!form.mobile_no.trim()) return 'Mobile number is required';
    return '';
  };

  // Helpers to assemble structured objects for Frappe custom_* fields
  const assemblePersonalInfo = (f) => {
    const ageNum = f.personal_age && Number.isFinite(Number(f.personal_age)) ? Number(f.personal_age) : undefined;
    const city = f.personal_city?.trim();
    const citizenship = f.personal_citizenship?.trim();
    const obj = {};
    if (ageNum !== undefined) obj.age = ageNum;
    if (city) obj.city = city;
    if (citizenship) obj.citizenship = citizenship;
    return Object.keys(obj).length ? obj : undefined;
  };
  const assembleEducation = (f) => {
    const level = f.education_level?.trim();
    const field = f.education_field?.trim();
    const yearNum = f.education_year && Number.isFinite(Number(f.education_year)) ? Number(f.education_year) : undefined;
    const institution = f.education_institution?.trim();
    const obj = {};
    if (level) obj.level = level;
    if (field) obj.field = field;
    if (yearNum !== undefined) obj.year = yearNum;
    if (institution) obj.institution = institution;
    return Object.keys(obj).length ? obj : undefined;
  };
  const assembleExperience = (f) => {
    const years = f.experience_years?.trim();
    const role = f.experience_role?.trim();
    const industry = f.experience_industry?.trim();
    const obj = {};
    if (years) obj.years = years;
    if (role) obj.role = role;
    if (industry) obj.industry = industry;
    return Object.keys(obj).length ? obj : undefined;
  };
  const assembleLanguage = (f) => {
    const english = f.lang_english?.trim();
    const german = f.lang_german?.trim();
    const ieltsNum = f.lang_ielts && Number.isFinite(Number(f.lang_ielts)) ? Number(f.lang_ielts) : undefined;
    const obj = {};
    if (english) obj.English = english;
    if (german) obj.German = german;
    if (ieltsNum !== undefined) obj.IELTS = ieltsNum;
    return Object.keys(obj).length ? obj : undefined;
  };
  const assembleFinancial = (f) => {
    const budget = f.financial_budget?.trim();
    const sponsor = f.financial_sponsor?.trim();
    const obj = {};
    if (budget) obj.budget = budget;
    if (sponsor) obj.sponsor = sponsor;
    return Object.keys(obj).length ? obj : undefined;
  };
  const assembleScoring = (f) => {
    const scoreNum = f.scoring_score && Number.isFinite(Number(f.scoring_score)) ? Number(f.scoring_score) : undefined;
    const notes = f.scoring_notes?.trim();
    const obj = {};
    if (scoreNum !== undefined) obj.score = scoreNum;
    if (notes) obj.notes = notes;
    return Object.keys(obj).length ? obj : undefined;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setResult({ type: 'error', message: err });
      return;
    }
    setSubmitting(true);
    setResult({ type: '', message: '' });
    try {
      // Build payload from structured inputs
      const payload = {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        mobile_no: form.mobile_no,
        notes: form.notes,
        custom_countryselection: form.custom_countryselection,
        custom_selected_pathway: form.custom_selected_pathway,
        custom_selected_package: form.custom_selected_package,
        custom_personal_info: assemblePersonalInfo(form),
        custom_education: assembleEducation(form),
        custom_experience: assembleExperience(form),
        custom_language_skills: assembleLanguage(form),
        custom_financial_info: assembleFinancial(form),
        custom_scoring_results: assembleScoring(form),
        custom_upload_cv: undefined,
      };
      // If CV file is provided, upload and attach metadata
      if (cvFile) {
        try {
          const { uploadCV } = await import('../services/wizardSubmissionService');
          const info = await uploadCV(cvFile, form.email || 'unknown');
          payload.custom_upload_cv = {
            url: info?.publicUrl,
            fileName: info?.fileName || cvFile.name,
            fileSize: info?.fileSize ?? cvFile.size,
            fileType: info?.fileType || cvFile.type,
            storage_path: info?.path,
          };
        } catch (e) {
          console.warn('CV upload failed:', e);
        }
      }
      const resp = await submitConsultationLead(payload);
      setResult({ type: 'success', message: 'Thank you! Your consultation request has been received. We will contact you shortly.' });
      setForm(initialForm);
      setCvFile(null);
      console.log('Frappe lead response:', resp);
    } catch (error) {
      const msg = error?.message || 'Failed to submit. Please try again.';
      setResult({ type: 'error', message: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Book Free Consultation | Ravana Migration</title>
        <meta name="description" content="Book a free consultation with Ravana Migration. Share your details and our team will contact you to discuss your study abroad goals." />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-indigo-100 py-10 px-4 sm:py-14">
        <div className="max-w-3xl mx-auto">
          <div className="card p-6 sm:p-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Book Free Consultation</h1>
            <p className="text-gray-600 mb-6">Fill in your details and we’ll reach out to schedule your free 15-minute consultation.</p>

            {result.message && (
              <div className={`mb-4 px-4 py-3 rounded ${result.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                {result.message}
              </div>
            )}

            <form onSubmit={onSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="first_name">First Name</label>
                  <input id="first_name" name="first_name" type="text" value={form.first_name} onChange={onChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="last_name">Last Name</label>
                  <input id="last_name" name="last_name" type="text" value={form.last_name} onChange={onChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="cvFile">Upload CV (optional)</label>
                  <input id="cvFile" type="file" accept=".pdf,.doc,.docx,.txt,.rtf" onChange={(e) => setCvFile(e.target.files?.[0] || null)} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400" />
                  {cvFile && (
                    <p className="text-xs text-gray-600 mt-1">Selected: {cvFile.name} ({Math.round(cvFile.size / 1024)} KB)</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="email">Email</label>
                  <input id="email" name="email" type="email" value={form.email} onChange={onChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="mobile_no">Mobile Number</label>
                  <input id="mobile_no" name="mobile_no" type="tel" value={form.mobile_no} onChange={onChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400" required />
                </div>
              </div>

              {/* Country Selection and quick notes */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="custom_countryselection">Country Selection</label>
                  <select id="custom_countryselection" name="custom_countryselection" value={form.custom_countryselection} onChange={onChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400">
                    <option value="">Select</option>
                    {countryOptions.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="notes">Notes (Optional)</label>
                  <input id="notes" name="notes" type="text" value={form.notes} onChange={onChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400" placeholder="E.g., Preferred time, questions" />
                </div>
              </div>

              {/* Advanced details (optional) */}
              <div className="mt-2">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Optional Details</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Personal Info</label>
                    <div className="grid grid-cols-2 gap-2">
                      <input name="personal_age" type="number" min="0" placeholder="Age" value={form.personal_age} onChange={onChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                      <input name="personal_city" type="text" placeholder="City" value={form.personal_city} onChange={onChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                      <input name="personal_citizenship" type="text" placeholder="Citizenship" value={form.personal_citizenship} onChange={onChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 col-span-2" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Education</label>
                    <div className="grid grid-cols-2 gap-2">
                      <select name="education_level" value={form.education_level} onChange={onChange} className="w-full border border-gray-300 rounded-lg px-3 py-2">
                        <option value="">Level</option>
                        {degreeOptions.map((d) => <option key={d} value={d}>{d}</option>)}
                      </select>
                      <input name="education_field" type="text" placeholder="Field" value={form.education_field} onChange={onChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                      <input name="education_year" type="number" placeholder="Year" value={form.education_year} onChange={onChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                      <input name="education_institution" type="text" placeholder="Institution" value={form.education_institution} onChange={onChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Experience</label>
                    <div className="grid grid-cols-2 gap-2">
                      <select name="experience_years" value={form.experience_years} onChange={onChange} className="w-full border border-gray-300 rounded-lg px-3 py-2">
                        <option value="">Years</option>
                        {experienceYearsOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                      </select>
                      <input name="experience_role" type="text" placeholder="Role" value={form.experience_role} onChange={onChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                      <select name="experience_industry" value={form.experience_industry} onChange={onChange} className="w-full border border-gray-300 rounded-lg px-3 py-2">
                        <option value="">Industry</option>
                        {industryOptions.map((i) => <option key={i} value={i}>{i}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Language Skills</label>
                    <div className="grid grid-cols-2 gap-2">
                      <select name="lang_english" value={form.lang_english} onChange={onChange} className="w-full border border-gray-300 rounded-lg px-3 py-2">
                        <option value="">English (CEFR)</option>
                        {englishLevels.map((l) => <option key={l} value={l}>{l}</option>)}
                      </select>
                      <select name="lang_german" value={form.lang_german} onChange={onChange} className="w-full border border-gray-300 rounded-lg px-3 py-2">
                        <option value="">German (CEFR)</option>
                        {germanLevels.map((l) => <option key={l} value={l}>{l}</option>)}
                      </select>
                      <input name="lang_ielts" type="number" step="0.5" min="0" max="9" placeholder="IELTS" value={form.lang_ielts} onChange={onChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Financial Info</label>
                    <div className="grid grid-cols-2 gap-2">
                      <select name="financial_budget" value={form.financial_budget} onChange={onChange} className="w-full border border-gray-300 rounded-lg px-3 py-2">
                        <option value="">Budget</option>
                        {budgetOptions.map((b) => <option key={b} value={b}>{b}</option>)}
                      </select>
                      <select name="financial_sponsor" value={form.financial_sponsor} onChange={onChange} className="w-full border border-gray-300 rounded-lg px-3 py-2">
                        <option value="">Sponsor</option>
                        {sponsorOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Scoring Results</label>
                    <div className="grid grid-cols-2 gap-2">
                      <input name="scoring_score" type="number" min="0" placeholder="Score" value={form.scoring_score} onChange={onChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                      <input name="scoring_notes" type="text" placeholder="Notes" value={form.scoring_notes} onChange={onChange} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="custom_selected_pathway">Selected Pathway</label>
                    <select id="custom_selected_pathway" name="custom_selected_pathway" value={form.custom_selected_pathway} onChange={onChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400">
                      <option value="">Select</option>
                      {pathwayOptions.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="custom_selected_package">Selected Package</label>
                    <select id="custom_selected_package" name="custom_selected_package" value={form.custom_selected_package} onChange={onChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400">
                      <option value="">Select</option>
                      {packageOptions.map((pkg) => (
                        <option key={pkg} value={pkg}>{pkg}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <button type="submit" disabled={submitting} className="btn btn-accent px-6 py-3 disabled:opacity-60">
                  {submitting ? 'Booking…' : 'Book Free Consultation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ConsultationBookingPage;
